import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

type SidebarItem = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    to?: string;
    onClick?: () => void;
};

type SidebarContextType = {
    isOpen: boolean | null;
    items: SidebarItem[];
    footerItems: SidebarItem[];
    activeItem: string | null;
    openSidebar: () => void;
    closeSidebar: () => void;
    toggleSidebar: () => void;
    removeSidebar: () => void;
    setItems: (items: SidebarItem[]) => void;
    setFooterItems: (items: SidebarItem[]) => void;
    setActiveItem: (item: string | null) => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);


export const SidebarProvider = ({ children }: { children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState<boolean | null>(false);
    const [items, setItemsState] = useState<SidebarItem[]>([]);
    const [footerItems, setFooterItemsState] = useState<SidebarItem[]>([]);
    const [activeItem, setActiveItemState] = useState<string | null>(null);

    const openSidebar = () => setIsOpen(true);
    const closeSidebar = () => setIsOpen(false);
    const removeSidebar = () => { setIsOpen(null); };
    const toggleSidebar = () => setIsOpen((prev) => !prev);

    // Memoized setters
    const setItems = useCallback((items: SidebarItem[]) => setItemsState(items), []);
    const setFooterItems = useCallback((items: SidebarItem[]) => setFooterItemsState(items), []);
    const setActiveItem = useCallback((item: string | null) => setActiveItemState(item), []);

    return (
        <SidebarContext.Provider
            value={{
                isOpen,
                footerItems,
                items,
                activeItem,
                openSidebar,
                closeSidebar,
                removeSidebar,
                toggleSidebar,
                setItems,
                setFooterItems,
                setActiveItem,
            }}
        >
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = (): SidebarContextType => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};
